import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FiCalendar, FiArrowLeft, FiShare2 } from "react-icons/fi";
import { toast } from "sonner";
import { SEO } from "@/components/seo";
import { Button } from "@/components/ui/button";
import { CardSkeleton } from "@/components/common";

type Post = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  featuredImage: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  publishedAt: string;
};

// Simple markdown-like rendering (basic)
function renderContent(content: string): string {
  return content
    // Headers
    .replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold mt-8 mb-4">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-2xl font-semibold mt-10 mb-4">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold mt-10 mb-4">$1</h1>')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary hover:underline">$1</a>')
    // Lists
    .replace(/^\- (.*$)/gim, '<li class="ml-4">$1</li>')
    .replace(/(<li.*<\/li>)/s, '<ul class="list-disc list-inside my-4">$1</ul>')
    // Paragraphs (double newlines)
    .replace(/\n\n/g, '</p><p class="mb-4">')
    // Wrap in paragraph
    .replace(/^(.+)$/gm, (match) => {
      if (match.startsWith('<')) return match;
      return match;
    });
}

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;

    fetch(`/api/blog/posts/${slug}`)
      .then(async (res) => {
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        const data = await res.json() as Post;
        setPost(data);
      })
      .catch((err) => {
        console.error("Failed to fetch post:", err);
        setNotFound(true);
      })
      .finally(() => setIsLoading(false));
  }, [slug]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: post?.title,
          url,
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16">
        <CardSkeleton />
        <div className="mt-8 space-y-4">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="font-serif text-3xl font-medium mb-4">Post Not Found</h1>
        <p className="text-muted-foreground mb-8">
          The blog post you're looking for doesn't exist or has been removed.
        </p>
        <Button asChild>
          <Link to="/blog">
            <FiArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={post.metaTitle || post.title}
        description={post.metaDescription || post.excerpt || undefined}
        url={`/blog/${post.slug}`}
        image={post.featuredImage || undefined}
        type="article"
      />

      <article>
        {/* Hero */}
        <header className="bg-primary text-primary-foreground py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <Link
              to="/blog"
              className="inline-flex items-center text-sm text-primary-foreground/90 hover:text-primary-foreground mb-6"
            >
              <FiArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>

            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium mb-6">
              {post.title}
            </h1>

            <div className="flex items-center gap-6 text-primary-foreground/80">
              <span className="flex items-center gap-2">
                <FiCalendar className="w-4 h-4" />
                {new Date(post.publishedAt).toLocaleDateString("en-AU", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 hover:text-primary-foreground transition-colors"
              >
                <FiShare2 className="w-4 h-4" />
                Share
              </button>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        {post.featuredImage && (
          <div className="max-w-4xl mx-auto px-4 -mt-8">
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-auto"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{
              __html: `<p class="mb-4">${renderContent(post.content)}</p>`,
            }}
          />
        </div>

        {/* Footer */}
        <div className="max-w-3xl mx-auto px-4 md:px-6 pb-16">
          <div className="border-t border-border pt-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <Button asChild variant="outline">
                <Link to="/blog">
                  <FiArrowLeft className="w-4 h-4 mr-2" />
                  Back to Blog
                </Link>
              </Button>
              <Button asChild>
                <Link to="/contact">Get in Touch</Link>
              </Button>
            </div>
          </div>
        </div>
      </article>
    </>
  );
}
